import React, { useState, useEffect } from 'react';
import { 
  Briefcase, Plus, Search, Filter, DollarSign, Clock, User, Building,
  ChevronRight, X, Loader2, Star, CheckCircle, AlertCircle, XCircle,
  TrendingUp, Calendar, MessageSquare, ExternalLink, MoreVertical
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const PIPELINE_STAGES = [
  { id: 'identified', label: 'Identified', color: 'bg-gray-500' },
  { id: 'researching', label: 'Researching', color: 'bg-blue-500' },
  { id: 'outreach', label: 'Outreach', color: 'bg-purple-500' },
  { id: 'negotiating', label: 'Negotiating', color: 'bg-yellow-500' },
  { id: 'contracting', label: 'Contracting', color: 'bg-orange-500' },
  { id: 'active', label: 'Active', color: 'bg-green-500' },
  { id: 'completed', label: 'Completed', color: 'bg-emerald-500' },
  { id: 'declined', label: 'Declined', color: 'bg-red-500' }
];

const CLIENT_STAGES = [
  { id: 'client_decision', label: 'Needs Your Decision', color: 'bg-[#C4A052]' },
  { id: 'client_interested', label: 'Interested', color: 'bg-green-500' },
  { id: 'info_requested', label: 'Info Requested', color: 'bg-blue-500' },
  { id: 'client_declined', label: 'Declined', color: 'bg-red-500' }
];

const PARTNERSHIP_TYPES = [
  'Ambassador', 'Content Partnership', 'Endorsement', 'Sponsorship', 
  'Equity Deal', 'Product Collaboration', 'Affiliate', 'Event Partnership'
];

const AEPartnerships = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [partnerships, setPartnerships] = useState([]);
  const [pipelineData, setPipelineData] = useState(null);
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState('list'); // list, pipeline

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchPartnerships();
    }
  }, [userEmail, filterStatus]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
      if (data.role === 'admin') {
        fetchPipeline();
      }
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchPartnerships = async () => {
    try {
      let url = `${API}/api/always-elevate/partnerships?user_email=${encodeURIComponent(userEmail)}`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setPartnerships(data.data);
      }
    } catch (err) {
      console.error('Error fetching partnerships:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPipeline = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/partnerships/pipeline?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setPipelineData(data.data);
      }
    } catch (err) {
      console.error('Error fetching pipeline:', err);
    }
  };

  const handleRespond = async (partnershipId, response) => {
    try {
      const res = await fetch(
        `${API}/api/always-elevate/partnerships/${partnershipId}/respond?user_email=${encodeURIComponent(userEmail)}&response=${response}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data.success) {
        fetchPartnerships();
        setSelectedPartnership(null);
      }
    } catch (err) {
      console.error('Error responding:', err);
    }
  };

  const filteredPartnerships = partnerships.filter(p => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return p.brand_name?.toLowerCase().includes(search) ||
             p.industry?.toLowerCase().includes(search) ||
             p.partnership_type?.toLowerCase().includes(search);
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const stage = [...PIPELINE_STAGES, ...CLIENT_STAGES].find(s => s.id === status);
    if (!stage) return null;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${stage.color} text-white`}>
        {stage.label}
      </span>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-partnerships-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">
                {isAdmin ? 'ELLE MAXWELL AGENCY' : 'ALWAYS ELEVATE'}
              </p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-partnerships-title">
                {isAdmin ? 'Partnership CRM' : 'Partnership Opportunities'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {partnerships.length} {isAdmin ? 'partnerships in pipeline' : 'opportunities for review'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <>
                  <div className="flex items-center bg-white/5 rounded-lg p-1">
                    <button
                      onClick={() => setView('list')}
                      className={`px-3 py-1.5 rounded text-sm ${view === 'list' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                    >
                      List
                    </button>
                    <button
                      onClick={() => setView('pipeline')}
                      className={`px-3 py-1.5 rounded text-sm ${view === 'pipeline' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                    >
                      Pipeline
                    </button>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                    data-testid="create-partnership-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Partnership
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats Row (Admin Only) */}
        {isAdmin && pipelineData && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Briefcase className="w-5 h-5 text-[#C4A052]" />
              </div>
              <p className="text-2xl font-bold">{pipelineData.total_prospects}</p>
              <p className="text-xs text-gray-400">Active Prospects</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold">{formatCurrency(pipelineData.active_value)}</p>
              <p className="text-xs text-gray-400">Active Value</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold">
                {pipelineData.pipeline?.find(p => p.stage === 'negotiating')?.count || 0}
              </p>
              <p className="text-xs text-gray-400">In Negotiation</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-2xl font-bold">
                {pipelineData.pipeline?.find(p => p.stage === 'active')?.count || 0}
              </p>
              <p className="text-xs text-gray-400">Active Deals</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search partnerships..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
              data-testid="partnership-search"
            />
          </div>
          
          <div className="flex items-center gap-2">
            {(isAdmin ? PIPELINE_STAGES.slice(0, 6) : CLIENT_STAGES).map(stage => (
              <button
                key={stage.id}
                onClick={() => setFilterStatus(filterStatus === stage.id ? 'all' : stage.id)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterStatus === stage.id 
                    ? `${stage.color} text-white` 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {stage.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pipeline View (Admin) */}
        {isAdmin && view === 'pipeline' && pipelineData && (
          <div className="grid grid-cols-6 gap-3 mb-8" data-testid="pipeline-view">
            {PIPELINE_STAGES.slice(0, 6).map(stage => {
              const stageData = pipelineData.pipeline?.find(p => p.stage === stage.id);
              const stagePartnerships = filteredPartnerships.filter(p => p.status === stage.id);
              
              return (
                <div key={stage.id} className="bg-black border border-white/20 rounded-xl overflow-hidden">
                  <div className={`${stage.color} px-3 py-2`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">{stage.label}</span>
                      <span className="text-xs bg-black/20 px-2 py-0.5 rounded-full text-white">
                        {stageData?.count || 0}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 space-y-2 max-h-96 overflow-y-auto">
                    {stagePartnerships.map(p => (
                      <div
                        key={p.id}
                        onClick={() => setSelectedPartnership(p)}
                        className="bg-black border border-white/20 rounded-lg p-3 cursor-pointer transition-colors"
                      >
                        <p className="font-medium text-sm truncate">{p.brand_name}</p>
                        <p className="text-xs text-gray-400 mt-1">{p.partnership_type}</p>
                        {p.value && (
                          <p className="text-xs text-[#C4A052] mt-1">{formatCurrency(p.value)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {(view === 'list' || !isAdmin) && (
          <>
            {filteredPartnerships.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Partnerships Found</h3>
                <p className="text-gray-500 text-sm">
                  {isAdmin ? 'Add your first partnership opportunity' : 'No opportunities requiring your attention'}
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="partnerships-list">
                {filteredPartnerships.map(partnership => (
                  <div
                    key={partnership.id}
                    onClick={() => setSelectedPartnership(partnership)}
                    className="bg-black border border-white/20 rounded-xl p-5 cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#C4A052]/20 flex items-center justify-center flex-shrink-0">
                          <Building className="w-6 h-6 text-[#C4A052]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{partnership.brand_name}</h3>
                            {getStatusBadge(partnership.status)}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{partnership.industry}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>{partnership.partnership_type}</span>
                            {partnership.value && (
                              <span className="text-[#C4A052]">{formatCurrency(partnership.value)}</span>
                            )}
                            {partnership.deadline && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Due {new Date(partnership.deadline).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                    
                    {/* Client Decision Actions */}
                    {!isAdmin && partnership.status === 'client_decision' && (
                      <div className="mt-4 pt-4 border-t border-white/10 flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRespond(partnership.id, 'interested'); }}
                          className="flex-1 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-colors"
                        >
                          Interested
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRespond(partnership.id, 'need_info'); }}
                          className="flex-1 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-colors"
                        >
                          Need Info
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRespond(partnership.id, 'not_interested'); }}
                          className="flex-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors"
                        >
                          Pass
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Detail Modal */}
      {selectedPartnership && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">{selectedPartnership.brand_name}</h3>
                <p className="text-gray-400 text-sm">{selectedPartnership.industry}</p>
              </div>
              <button onClick={() => setSelectedPartnership(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status & Type */}
              <div className="flex items-center gap-3">
                {getStatusBadge(selectedPartnership.status)}
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">{selectedPartnership.partnership_type}</span>
              </div>

              {/* Description */}
              {selectedPartnership.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                  <p className="text-gray-300">{selectedPartnership.description}</p>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                {selectedPartnership.value && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Value</h4>
                    <p className="text-[#C4A052] font-semibold">{formatCurrency(selectedPartnership.value)}</p>
                  </div>
                )}
                {selectedPartnership.deadline && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Decision Deadline</h4>
                    <p>{new Date(selectedPartnership.deadline).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedPartnership.time_commitment && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Time Commitment</h4>
                    <p>{selectedPartnership.time_commitment}</p>
                  </div>
                )}
                {selectedPartnership.compensation_type && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Compensation</h4>
                    <p>{selectedPartnership.compensation_type}</p>
                  </div>
                )}
              </div>

              {/* Deliverables */}
              {selectedPartnership.deliverables && selectedPartnership.deliverables.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Deliverables</h4>
                  <ul className="space-y-1">
                    {selectedPartnership.deliverables.map((d, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-[#C4A052]" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Fit Scores */}
              {selectedPartnership.fit_scores && Object.keys(selectedPartnership.fit_scores).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Brand Fit Analysis</h4>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(selectedPartnership.fit_scores).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <div className="w-10 h-10 mx-auto rounded-full bg-[#C4A052]/20 flex items-center justify-center mb-1">
                          <span className="text-[#C4A052] font-bold">{value}</span>
                        </div>
                        <p className="text-xs text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Elle's Recommendation */}
              {selectedPartnership.elle_recommendation && (
                <div className="bg-[#C4A052]/10 border border-[#C4A052]/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-[#C4A052] mb-2">Elle&apos;s Recommendation</h4>
                  <p className="text-gray-300 text-sm">{selectedPartnership.elle_recommendation}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setSelectedPartnership(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
              {!isAdmin && selectedPartnership.status === 'client_decision' && (
                <>
                  <button
                    onClick={() => handleRespond(selectedPartnership.id, 'interested')}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                  >
                    I&apos;m Interested
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal (Admin) */}
      {showCreateModal && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Add Partnership</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-400 text-sm">Partnership creation form coming soon.</p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEPartnerships;
